import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';

const BENCHMARK_COLORS = {
  exceeding: '#10B981',
  meeting: '#3B82F6',
  approaching: '#F59E0B',
  below: '#EF4444',
};

const BENCHMARK_LABELS = {
  exceeding: 'Exceeding (85%+)',
  meeting: 'Meeting (70-84%)',
  approaching: 'Approaching (50-69%)',
  below: 'Below (<50%)',
};

export function BenchmarkBarChart({ data, height = 200 }) {
  if (!data) return null;

  const chartData = [
    { name: 'Exceeding', value: data.exceeding || 0, level: 'exceeding' },
    { name: 'Meeting', value: data.meeting || 0, level: 'meeting' },
    { name: 'Approaching', value: data.approaching || 0, level: 'approaching' },
    { name: 'Below', value: data.below || 0, level: 'below' },
  ];

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = total > 0 ? Math.round((data.value / total) * 100) : 0;
      return (
        <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
          <p className="font-medium text-gray-900">{data.name}</p>
          <p className="text-lg font-bold" style={{ color: BENCHMARK_COLORS[data.level] }}>
            {data.value} students ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6B7280' }} />
        <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} allowDecimals={false} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={BENCHMARK_COLORS[entry.level]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function BenchmarkPieChart({ data, height = 250 }) {
  if (!data) return null;

  const chartData = [
    { name: 'Exceeding', value: data.exceeding || 0, level: 'exceeding' },
    { name: 'Meeting', value: data.meeting || 0, level: 'meeting' },
    { name: 'Approaching', value: data.approaching || 0, level: 'approaching' },
    { name: 'Below', value: data.below || 0, level: 'below' },
  ].filter(item => item.value > 0);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (value === 0) return null;

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight="bold">
        {value}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={renderCustomLabel}
          outerRadius={80}
          dataKey="value"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={BENCHMARK_COLORS[entry.level]} />
          ))}
        </Pie>
        <Legend
          formatter={(value, entry) => (
            <span className="text-sm text-gray-700">
              {value} ({Math.round((entry.payload.value / total) * 100)}%)
            </span>
          )}
        />
        <Tooltip
          formatter={(value, name) => [`${value} students (${Math.round((value / total) * 100)}%)`, name]}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default BenchmarkBarChart;
